import Nav, { NavItem } from "./Nav";
import { Skeleton } from "primereact/skeleton";
import { IAssetTypesResponse } from "../models/asset";
import { useQuery } from "@tanstack/react-query";
import useAuthHttp from "../hooks/useAuthHttp";
import { useRef, useState } from "react";
import { groupAssetListTypeByGroupName } from "../commons/group-assets-list-type";
import PrivateBondsHandleForm from "./assets/PrivateBondsHandleForm";
import SavingAccountHandleForm from "./assets/SavingAccountHandleForm";
import { Toast } from "primereact/toast";
import TreasureBoundsHandleForm from "./assets/TreasureBoundsHandleForm";
import CheckingAccoundHandleForm from "./assets/CheckingAccountHandleForm";
import CustomizedHandleForm from "./assets/CustomizedHandleForm";
import PropertyHandleForm from "./assets/PropertyHandleForm";
import VehicleHandleForm from "./assets/VehicleHandleForm";
import DebentureHandleForm from "./assets/DebentureHandleForm";
import AportHandleForm from "./assets/AportHandleForm";
import Container from "./Container";
import WithdrawHandleForm from "./assets/WithdrawHandleForm";
import InsertCustomizedEventHandleForm from "./assets/InsertCustomizedEventHandleForm";
import CurrencyHandleForm from "./assets/CurrencyHandleForm";
import CriptoHandleForm from "./assets/CriptoHandleForm";
import FundHandleForm from "./assets/FundHandleForm";
import StockHandleForm from "./assets/StockHandleForm";
import { Dialog } from "primereact/dialog";
import {
    assetListGroupToAnalyticsType,
    trackAssetCreateCompleted,
    trackAssetCreateStarted,
    trackChargeStarted,
    trackWithdrawStarted,
    Tracker,
} from "../analytics";
import type { AssetType as AnalyticsAssetType } from "../analytics";
import { displayAssetGroupName } from "../commons/asset-group-label";

enum AssetInsertNavOptions {
    InsertAsset = "InsertAsset",
    Aporte = "Aporte",
    Resgate = "Resgate",
    InsertEvent = "InsertEvent"
}

enum AssetListType {
    CDB = "CDB,CRA,CRI,LC,LCA,LCI,LF",
    Poupanca = "Poupança",
    TesouroDireto = "Tesouro Direto",
    ContaCorrente = "Conta Corrente",
    Personalizado = "Personalizado",
    Imoveis = "Imóveis",
    Veiculo = "Veículo",
    Debenture = "Debênture",
    Moedas = "Moedas",
    Cripto = "Cripto",
    Funds = "Fundos de Investimento",
    Stocks = "Ações",
}

export interface IAssetHandleSidebarProps {
    isOpen: boolean;
    toggle: (isOpen: boolean) => void;
}

export default function AssetHandleSidebar({ isOpen, toggle }: IAssetHandleSidebarProps) {
    const [assetInsertNavOption, setAssetInsertNavOption] = useState<AssetInsertNavOptions | null>(null);
    const [assetListType, setAssetListType] = useState<{ type: AssetListType; options: { name: string; id: string; }[]; } | null>(null)
    const [activeAnalyticsAssetType, setActiveAnalyticsAssetType] = useState<AnalyticsAssetType | null>(null);
    const toast = useRef<Toast | null>(null);
    const authHttp = useAuthHttp();
    const assetListTypeQuery = useQuery({
        queryKey: ["assetListType"],
        queryFn: async () =>
            await authHttp.doGet<IAssetTypesResponse>('asset/list_types')
    });
    const assetListTypeGroupped = groupAssetListTypeByGroupName(assetListTypeQuery.data?.asset_list ?? []);

    const handleCreateSuccess = (assetType: AnalyticsAssetType) => {
        trackAssetCreateCompleted(assetType);
        setActiveAnalyticsAssetType(null);
        setAssetListType(null);
        toast.current?.show({
            severity: "success",
            summary: "Investimento cadastrado com sucesso"
        });
    };

    const handlePropertySuccess = () => {
        trackAssetCreateCompleted('property');
        setActiveAnalyticsAssetType(null);
        setAssetListType(null);
        toast.current?.show({
            severity: "success",
            summary: "Propriedade cadastrada com sucesso"
        });
    };

    const handleVehicleSuccess = () => {
        trackAssetCreateCompleted('vehicle');
        setActiveAnalyticsAssetType(null);
        setAssetListType(null);
        toast.current?.show({
            severity: "success",
            summary: "Veículo cadastrado com sucesso"
        });
    };

    const closeSidebar = () => {
        if (activeAnalyticsAssetType) {
            Tracker.trackAssetCreate('abandoned', activeAnalyticsAssetType);
            setActiveAnalyticsAssetType(null);
        }
        setAssetInsertNavOption(null);
        setAssetListType(null);
        toggle(false);
    };

    function renderHandleComponentByType(type?: AssetListType) {
        switch (type) {
            case AssetListType.CDB:
                return (
                    <PrivateBondsHandleForm
                        onSuccessRegister={() => handleCreateSuccess('private_bonds')}
                        goBack={() => setAssetListType(null)}
                        assetId=""
                    />
                );
            case AssetListType.Poupanca:
                return (
                    <SavingAccountHandleForm
                        onSuccessRegister={() => handleCreateSuccess('saving_account')}
                        goBack={() => setAssetListType(null)}
                        assetId=""
                    />
                );
            case AssetListType.TesouroDireto:
                return (
                    <TreasureBoundsHandleForm
                        onSuccessRegister={() => handleCreateSuccess('treasure_bounds')}
                        goBack={() => setAssetListType(null)}
                        assetId=""
                    />
                );
            case AssetListType.ContaCorrente:
                return (
                    <CheckingAccoundHandleForm
                        onSuccessRegister={() => handleCreateSuccess('checking_account')}
                        goBack={() => setAssetListType(null)}
                        assetId=""
                    />
                );
            case AssetListType.Personalizado:
                return (
                    <CustomizedHandleForm
                        onSuccessRegister={() => handleCreateSuccess('customized')}
                        goBack={() => setAssetListType(null)}
                        assetId=""
                    />
                );
            case AssetListType.Imoveis:
                return (
                    <PropertyHandleForm
                        onSuccessRegister={handlePropertySuccess}
                        goBack={() => setAssetListType(null)}
                        assetId=""
                    />
                );
            case AssetListType.Veiculo:
                return (
                    <VehicleHandleForm
                        onSuccessRegister={handleVehicleSuccess}
                        goBack={() => setAssetListType(null)}
                        assetId=""
                    />
                );
            case AssetListType.Debenture:
                return (
                    <DebentureHandleForm
                        onSuccessRegister={() => handleCreateSuccess('debenture')}
                        goBack={() => setAssetListType(null)}
                        assetId=""
                    />
                );
            case AssetListType.Moedas:
                return (
                    <CurrencyHandleForm
                        onSuccessRegister={() => handleCreateSuccess('currency')}
                        goBack={() => setAssetListType(null)}
                        assetId=""
                    />
                );
            case AssetListType.Cripto:
                return (
                    <CriptoHandleForm
                        onSuccessRegister={() => handleCreateSuccess('cripto')}
                        goBack={() => setAssetListType(null)}
                        assetId=""
                    />
                );
            case AssetListType.Funds:
                return (
                    <FundHandleForm
                        onSuccessRegister={() => handleCreateSuccess('fund')}
                        goBack={() => setAssetListType(null)}
                        assetId=""
                    />
                );
            case AssetListType.Stocks:
                return (
                    <StockHandleForm
                        onSuccessRegister={() => handleCreateSuccess('stock')}
                        goBack={() => setAssetListType(null)}
                        assetId=""
                    />
                );
            default: {
                const groupName = String(type ?? "");
                if (groupName.startsWith("Fundos")) {
                    return (
                        <FundHandleForm
                            onSuccessRegister={() => handleCreateSuccess('fund')}
                            goBack={() => setAssetListType(null)}
                            assetId=""
                        />
                    );
                }
                return <></>;
            }
        }
    }

    return (
        <>
            <Toast ref={toast} />
            <Dialog
                visible={isOpen}
                onHide={closeSidebar}
                modal
                style={{ width: "50rem" }}
            >
                {
                    !assetInsertNavOption
                    && (
                        <Nav>
                            <NavItem
                                id={AssetInsertNavOptions.InsertAsset}
                                onSelectOption={(id) => setAssetInsertNavOption(id as AssetInsertNavOptions)}
                            >
                                Inserir novo investimento
                            </NavItem>
                            <NavItem
                                id={AssetInsertNavOptions.Aporte}
                                onSelectOption={(id) => {
                                    trackChargeStarted();
                                    setAssetInsertNavOption(id as AssetInsertNavOptions);
                                }}
                            >
                                Aporte
                            </NavItem>
                            <NavItem
                                id={AssetInsertNavOptions.Resgate}
                                onSelectOption={(id) => {
                                    trackWithdrawStarted();
                                    setAssetInsertNavOption(id as AssetInsertNavOptions);
                                }}
                            >
                                Resgate
                            </NavItem>
                            <NavItem
                                id={AssetInsertNavOptions.InsertEvent}
                                onSelectOption={(id) => setAssetInsertNavOption(id as AssetInsertNavOptions)}
                            >
                                Cadastrar evento personalizado
                            </NavItem>
                        </Nav>
                    )
                }

                {
                    assetListType && (
                        <>
                            {renderHandleComponentByType(assetListType?.type)}
                        </>
                    )
                }

                {
                    assetInsertNavOption == AssetInsertNavOptions.InsertAsset
                    && !assetListType
                    && (
                        <Nav>
                            <>
                                <NavItem
                                    id="go-back"
                                    showPrefixIcon
                                    onSelectOption={() => setAssetInsertNavOption(null)}
                                >
                                    Voltar
                                </NavItem>

                                {
                                    assetListTypeQuery.isLoading
                                    && (
                                        <>
                                            <Skeleton height="30px" width="100%" />
                                            <Skeleton height="30px" width="100%" />
                                            <Skeleton height="30px" width="100%" />
                                            <Skeleton height="30px" width="100%" />
                                            <Skeleton height="30px" width="100%" />
                                        </>
                                    )
                                }

                                {
                                    !assetListTypeQuery.isLoading && assetListTypeGroupped.map(asset => {
                                        return (
                                            <NavItem
                                                key={asset.groupName}
                                                id={asset.groupName}
                                                onSelectOption={(item) => {
                                                    const analyticsType = assetListGroupToAnalyticsType(item);
                                                    if (analyticsType) {
                                                        trackAssetCreateStarted(analyticsType, 'asset_sidebar');
                                                        setActiveAnalyticsAssetType(analyticsType);
                                                    }
                                                    setAssetListType({
                                                        type: item as AssetListType,
                                                        options: asset.names
                                                    });
                                                }}
                                            >
                                                {displayAssetGroupName(asset.groupName)}
                                            </NavItem>
                                        );
                                    })
                                }
                            </>
                        </Nav>
                    )
                }

                {
                    assetInsertNavOption == AssetInsertNavOptions.Aporte
                    && !assetListType
                    && (
                        <Nav>
                            <Container flexColumn gap={5}>
                                <NavItem
                                    id="go-back"
                                    showPrefixIcon
                                    onSelectOption={() => setAssetInsertNavOption(null)}
                                >
                                    Voltar
                                </NavItem>

                                <AportHandleForm
                                    onSuccess={() => {
                                        setAssetInsertNavOption(null);

                                        toast.current?.show({
                                            severity: "success",
                                            summary: "Aporte inserido com sucesso"
                                        });
                                    }}
                                />
                            </Container>
                        </Nav>
                    )
                }

                {
                    assetInsertNavOption == AssetInsertNavOptions.Resgate
                    && !assetListType
                    && (
                        <Nav>
                            <Container flexColumn gap={5}>
                                <NavItem
                                    id="go-back"
                                    showPrefixIcon
                                    onSelectOption={() => setAssetInsertNavOption(null)}
                                >
                                    Voltar
                                </NavItem>

                                <WithdrawHandleForm
                                    onSuccess={() => {
                                        setAssetInsertNavOption(null);

                                        toast.current?.show({
                                            severity: "success",
                                            summary: "Resgate feito com sucesso"
                                        });
                                    }}
                                />
                            </Container>
                        </Nav>
                    )
                }

                {
                    assetInsertNavOption == AssetInsertNavOptions.InsertEvent
                    && !assetListType
                    && (
                        <Nav>
                            <Container flexColumn gap={5}>
                                <NavItem
                                    id="go-back"
                                    showPrefixIcon
                                    onSelectOption={() => setAssetInsertNavOption(null)}
                                >
                                    Voltar
                                </NavItem>

                                <InsertCustomizedEventHandleForm
                                    onSuccess={() => {
                                        setAssetInsertNavOption(null);

                                        toast.current?.show({
                                            severity: "success",
                                            summary: "Evento personalizado inserido com sucesso"
                                        });
                                    }}
                                />
                            </Container>
                        </Nav>
                    )
                }
            </Dialog>
        </>
    )
}